import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SlidersHorizontal } from 'lucide-react';
import type { SettingToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useLoyaltySettingsList, useLoyaltySettingMutations } from '~/hooks/use-loyalty-settings';
import { tokenToForm, type SettingFormValues } from '~/lib/schemas/setting';
import { APPOINTMENTS_MODULE, useHasSpecialModule } from '~/lib/auth/use-has-special-module';
import { isAppointmentsLoyaltySetting, isKnownLoyaltySetting } from '~/lib/loyalty-settings';
import { LoyaltySettingEditDialog } from './settings-edit-dialog';

/** Loyalty-points settings overview (Παράμετροι) — edit/delete the LoyaltyPoints param group. */
export function LoyaltyOverviewGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useLoyaltySettingsList();
	const { remove } = useLoyaltySettingMutations();
	const appointments = useHasSpecialModule(APPOINTMENTS_MODULE);
	const rows = useMemo(() => (data ?? []).filter(s => appointments || !isAppointmentsLoyaltySetting(s.pn)), [data, appointments]);

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<SettingFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<SettingToken | null>(null);

	const columns = useMemo<SystemGridColumn<SettingToken>[]>(
		() => [
			{
				key: 'name',
				header: t('loyalty.overview.columns.name'),
				width: '280px',
				sortValue: s => s.pn,
				exportValue: s => s.pn,
				render: s => (
					<span className="truncate font-mono text-xs font-medium text-foreground" title={s.pn}>
						{s.pn}
					</span>
				)
			},
			{
				key: 'description',
				header: t('loyalty.overview.columns.description'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.descr ?? '',
				exportValue: s => s.descr ?? '',
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.descr ?? ''}>
						{s.descr || '—'}
					</span>
				)
			},
			{
				key: 'value',
				header: t('loyalty.overview.columns.value'),
				width: '200px',
				sortValue: s => s.pv ?? '',
				exportValue: s => s.pv ?? '',
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.pv ?? ''}>
						{s.pv || '—'}
					</span>
				)
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SettingToken>
				gridId="loyalty-overview"
				title={t('loyalty.overview.title')}
				rows={rows}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.pn}
				columns={columns}
				searchPredicate={(s, q) => `${s.pn ?? ''} ${s.pv ?? ''} ${s.descr ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<SlidersHorizontal size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				onEdit={s => {
					setEditing(tokenToForm(s));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				canDelete={s => !isKnownLoyaltySetting(s.pn)}
				exportFilename="loyalty-settings"
			/>

			<LoyaltySettingEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('loyalty.overview.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('loyalty.overview.deleteBody', { name: deleteRow?.pn ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
